import { useEffect, useRef, useState } from 'react';
import { isEmpty, map, uniqBy } from 'lodash';

import { formatAsset, getRequestOptions, sortAsset } from '../utils';

import { jsonApi } from '~/clients';
import { jsonaDataFormatter } from '~/utils/jsonaDataFormatter';
import { PER_PAGE } from '~/components/AssetEditor/constants';
import { ASSET_TYPES } from '~/constants';

export function usePrimaryAssetSelectData({
  appKey,
  assetType,
  company,
  groups,
  isRequiredAssetId,
  parentAssetId,
  parentAssetType,
  searchValue,
  assetFields,
  currentValue,
  setCurrentOption,
  scrollPage,
  onActiveAssetIdChange,
  isDrillingPlatformApp,
}) {
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(false);
  const prevCompany = useRef(null);
  const prevAssetType = useRef(null);
  const prevSearchValue = useRef(null);
  const prevScrollPage = useRef(null);

  const assetFieldsByAssetType =
    assetType === ASSET_TYPES.pad.type
      ? [...assetFields, 'pad.active', 'pad.parent_frac_fleet_id']
      : assetFields;

  const loadAssets = async (group, shouldAppend = false) => {
    try {
      const currentAssetType = group?.type || assetType;
      setLoading(true);
      const requestParams = { per_page: PER_PAGE, page: scrollPage };
      if (parentAssetType && parentAssetId) requestParams[parentAssetType] = parentAssetId;
      if (company && company !== 'all') requestParams.company = company;
      if (searchValue) requestParams.search = searchValue;
      if (appKey) requestParams.app_key = appKey;

      prevCompany.current = company;
      const requestPromise = jsonApi.mapAssetGetRequest(currentAssetType)({
        ...getRequestOptions(currentAssetType, assetFieldsByAssetType),
        ...requestParams,
      });

      const response = await requestPromise;
      const deserializedResponse = jsonaDataFormatter.deserialize(response);

      const responseAssets = map(deserializedResponse, asset =>
        formatAsset({
          asset,
          group: group?.name,
          isRequiredAssetId,
          isPrimaryAsset: true,
          isDrillingPlatformApp,
        })
      ).sort(sortAsset);

      if (prevCompany.current === company) {
        if (shouldAppend) {
          setAssets(prevState => uniqBy([...prevState, ...responseAssets], 'id'));
        } else {
          setAssets(responseAssets);
        }
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const loadCurrentOption = async () => {
    let response = await jsonApi.mapAssetGetRequest(assetType)({
      ...getRequestOptions(assetType, assetFieldsByAssetType),
      ids: currentValue,
    });
    if (isEmpty(response.data)) {
      // NOTE: Try fetch current option from v2/assets
      response = await jsonApi.getAssets({
        ...getRequestOptions(assetType, assetFieldsByAssetType),
        ids: currentValue,
      });
    }
    const deserializedResponse = jsonaDataFormatter.deserialize(response);

    const formattedAsset = formatAsset({
      asset: deserializedResponse[0],
      isPrimaryAsset: true,
      isRequiredAssetId,
      isDrillingPlatformApp,
    });

    setCurrentOption(formattedAsset);
    if (assetType === ASSET_TYPES.rig.type) {
      const activeWellId = isDrillingPlatformApp
        ? Number(response?.included?.[0]?.id)
        : formattedAsset?.activeWellId || null;
      onActiveAssetIdChange(activeWellId);
    } else if (assetType === ASSET_TYPES.intervention_unit.type) {
      const activeWellId = formattedAsset?.activeWellId || null;
      onActiveAssetIdChange(activeWellId);
    }
  };

  useEffect(() => {
    if (company) {
      setAssets([]);
    }
  }, [company]);

  useEffect(() => {
    // Check if searchValue changed - if so, reset assets and don't append
    const searchValueChanged = prevSearchValue.current !== searchValue;
    const scrollPageChanged = prevScrollPage.current !== scrollPage;
    // Only append for pagination: searchValue didn't change AND scrollPage increased
    const isPagination =
      !searchValueChanged &&
      scrollPageChanged &&
      scrollPage > 1 &&
      (prevScrollPage.current || 0) < scrollPage;

    if (searchValueChanged) {
      prevSearchValue.current = searchValue;
    }
    if (scrollPageChanged) {
      prevScrollPage.current = scrollPage;
    }

    if (groups) {
      groups.forEach(group => {
        loadAssets(group, isPagination);
      });
      return;
    }
    if (!!assetType && !!prevAssetType.current && prevAssetType.current !== assetType) {
      setAssets([]);
    }
    prevAssetType.current = assetType;
    loadAssets(null, isPagination);
  }, [
    assetType,
    parentAssetType,
    parentAssetId,
    company,
    appKey,
    groups?.length,
    searchValue,
    scrollPage,
  ]);

  useEffect(() => {
    if (currentValue) {
      loadCurrentOption();
    }
  }, []);

  return { assets, loading };
}
